import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, RotateCcw, MessageSquare, X, Truck, Printer, Search, Receipt, Star, ImagePlus, CheckCircle2 } from "lucide-react";
import { ReferralBanner } from "@/components/ReferralBanner";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty, money } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";

export function Stars({ value = 0, size = 16, onSelect }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} onClick={onSelect ? () => onSelect(n) : undefined}
          style={{ cursor: onSelect ? "pointer" : "default", color: n <= Math.round(value) ? "var(--gold)" : "var(--line, #d9d5c8)" }}
          fill={n <= Math.round(value) ? "var(--gold)" : "none"} data-testid={onSelect ? `star-${n}` : undefined} />
      ))}
    </span>
  );
}

async function fileToReviewPhoto(file, max = 1000) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function ReviewModal({ order, onClose, onDone }) {
  const [rating, setRating] = useState(order.review?.rating || 0);
  const [text, setText] = useState(order.review?.text || "");
  const [photo, setPhoto] = useState(order.review?.photo || null);
  const [saving, setSaving] = useState(false);
  const pickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setPhoto(await fileToReviewPhoto(f)); } catch { toast.error("Couldn't read that image"); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) { toast.error("Pick a star rating"); return; }
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/review`, { rating, text, photo });
      toast.success("Thanks for your review!");
      onDone();
    } catch (err) { toast.error(err.message || "Could not save review"); }
    finally { setSaving(false); }
  };
  return (
    <div className="ss-modal-back" onClick={onClose}>
      <form className="ss-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="review-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ fontSize: 20 }}>{order.review ? "Edit your review" : "Write a review"}</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose}><X size={18} /></button>
        </div>
        <p className="ss-muted" style={{ fontSize: 14, marginBottom: 14 }}>{order.item_title} · Sold by {order.seller_name}</p>
        <div style={{ marginBottom: 14 }}><Stars value={rating} size={30} onSelect={setRating} /></div>
        <label className="ss-label">Your review (optional)</label>
        <div className="ss-field" style={{ height: "auto", padding: 12, alignItems: "flex-start" }}>
          <textarea rows={4} placeholder="How was the item and the seller?" value={text} onChange={(e) => setText(e.target.value)} style={{ width: "100%", border: "none", outline: "none", background: "transparent", resize: "vertical", fontFamily: "var(--font-body)", fontSize: 15, color: "var(--text)" }} data-testid="review-text" />
        </div>
        <label className="ss-label" style={{ marginTop: 14 }}>Add a photo (optional)</label>
        {photo ? (
          <div style={{ position: "relative", width: 120, height: 120, marginTop: 6 }}>
            <img src={photo} alt="review" style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 12 }} data-testid="review-photo-preview" />
            <button type="button" onClick={() => setPhoto(null)} data-testid="review-photo-remove" className="disc-search" style={{ position: "absolute", top: -8, right: -8, width: 28, height: 28 }}><X size={15} /></button>
          </div>
        ) : (
          <label className="ss-btn ss-btn-ghost ss-btn-sm" style={{ marginTop: 6, display: "inline-flex", cursor: "pointer" }} data-testid="review-photo-btn">
            <ImagePlus size={15} /> Upload photo
            <input type="file" accept="image/*" onChange={pickPhoto} style={{ display: "none" }} data-testid="review-photo-input" />
          </label>
        )}
        <button className="ss-btn ss-btn-primary ss-btn-block" style={{ marginTop: 16 }} disabled={saving} data-testid="review-submit">{saving ? "Saving…" : "Submit review"}</button>
      </form>
    </div>
  );
}

function printReceipt(o) {
  const w = window.open("", "_blank", "width=460,height=680");
  if (!w) { toast.error("Allow pop-ups to download the receipt"); return; }
  const date = o.created_at ? new Date(o.created_at).toLocaleString() : "";
  w.document.write(`<html><head><title>Receipt ${o.id}</title><style>
    body{font-family:Arial,sans-serif;padding:28px;color:#10241c}
    .box{border:1px solid #e5e2d8;border-radius:14px;padding:24px;max-width:400px;margin:auto}
    h1{font-size:22px;margin:0;color:#0c4a38}.muted{color:#8a8a7a;font-size:12px}
    .row{display:flex;justify-content:space-between;margin:8px 0;font-size:14px}
    .tot{border-top:2px solid #0c4a38;margin-top:14px;padding-top:12px;font-size:20px;font-weight:800;color:#0c4a38}
    .brand{font-size:13px;letter-spacing:2px;text-transform:uppercase;color:#d4a017;font-weight:800;margin-bottom:4px}
  </style></head><body><div class="box">
    <div class="brand">SouqStream</div><h1>Receipt</h1>
    <div class="muted">Order ${o.id} · ${date}</div>
    <div class="row" style="margin-top:16px"><span>Item</span><b>${o.item_title || ""}</b></div>
    <div class="row"><span>Seller</span><span>${o.seller_name || ""}</span></div>
    <div class="row"><span>Status</span><span>${(o.status || "").replace("_"," ")}</span></div>
    ${o.tracking_number ? `<div class="row"><span>Tracking</span><span>${o.tracking_number}</span></div>` : ""}
    <div class="row tot"><span>Total paid</span><span>$${Number(o.amount || 0).toFixed(2)}</span></div>
    <div class="muted" style="margin-top:18px">Thank you for shopping on SouqStream 💚</div>
  </div><script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "to_ship", label: "To ship" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
  { key: "refunded", label: "Refunded" },
  { key: "cancelled", label: "Cancelled" },
];

function printReturnLabel(o) {
  if (o.return_label_url) { window.open(o.return_label_url, "_blank"); return; }
  const w = window.open("", "_blank", "width=460,height=640");
  if (!w) { toast.error("Allow pop-ups to print the label"); return; }
  w.document.write(`<html><head><title>Return label ${o.return_tracking_number}</title><style>
    body{font-family:Arial,sans-serif;padding:24px;color:#10241c}
    .box{border:2px dashed #b4531f;border-radius:12px;padding:20px}
    h1{font-size:20px;margin:0 0 4px}.muted{color:#5c5c50;font-size:12px}
    .row{margin:10px 0}.big{font-size:22px;font-weight:800;letter-spacing:2px}
    .bar{height:56px;background:repeating-linear-gradient(90deg,#000 0 3px,#fff 3px 6px);margin-top:8px;border-radius:4px}
  </style></head><body><div class="box">
    <h1>PREPAID RETURN · ${o.return_carrier || "USPS"}</h1><div class="muted">${o.return_service || ""}</div>
    <div class="row"><div class="muted">RETURN OF</div><b>${o.item_title || ""}</b></div>
    <div class="row"><div class="muted">TRACKING</div><div class="big">${o.return_tracking_number || ""}</div><div class="bar"></div></div>
    <div class="muted">Attach to the parcel and drop off at any ${o.return_carrier || "USPS"} location within 14 days.</div>
  </div><script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
}

function ReturnModal({ order, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/purchases/${order.id}/return`, { reason });
      toast.success("Return requested — the seller has been notified.");
      onDone();
    } catch (err) {
      toast.error(err.message || "Could not request return");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="ss-modal-back" onClick={onClose}>
      <form className="ss-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="return-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ fontSize: 20 }}>Return or refund</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose}><X size={18} /></button>
        </div>
        <p className="ss-muted" style={{ fontSize: 14, marginBottom: 14 }}>{order.item_title} · {money(order.amount)}</p>
        <label className="ss-label">Reason (optional)</label>
        <div className="ss-field" style={{ height: "auto", padding: 12, alignItems: "flex-start" }}>
          <textarea rows={3} placeholder="Tell the seller what went wrong…" value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: "100%", border: "none", outline: "none", background: "transparent", resize: "vertical", fontFamily: "var(--font-body)", fontSize: 15, color: "var(--text)" }} data-testid="return-reason" />
        </div>
        <button className="ss-btn ss-btn-primary ss-btn-block" style={{ marginTop: 16 }} disabled={saving} data-testid="return-submit">{saving ? "Submitting…" : "Request return"}</button>
      </form>
    </div>
  );
}

export default function Purchases() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [returnOrder, setReturnOrder] = useState(null);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/purchases").then((d) => setOrders(d.orders)).catch(() => setOrders([]));
  React.useEffect(() => { load(); }, []);
  if (orders === null) return <Loading />;

  const confirmReceived = async (o) => {
    if (!window.confirm(`Confirm you received "${o.item_title}"? This closes the order and starts your 14-day return window.`)) return;
    try {
      await api.post(`/purchases/${o.id}/confirm-received`);
      toast.success("Marked as received — your 14-day return window is now open.");
      load();
    } catch (e) { toast.error(e.message || "Could not mark as received"); }
  };

  const filtered = orders.filter((o) => {
    if (filter !== "all" && o.status !== filter) return false;
    if (q.trim()) {
      const s = `${o.item_title || ""} ${o.seller_name || ""}`.toLowerCase();
      if (!s.includes(q.trim().toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="fade-up">
      <PageHead title="Your Purchases" sub="Everything you've won and bought on SouqStream." />
      {orders.length > 0 && <ReferralBanner style={{ marginBottom: 18 }} />}
      {orders.length === 0 ? (
        <Empty icon={ShoppingBag} title="No purchases yet" sub="Join a live show and grab your first deal!" />
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div className="ss-field" style={{ height: 44, flex: 1, minWidth: 220 }}>
              <Search size={16} className="ss-muted" />
              <input placeholder="Search by item or seller…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="purchases-search" />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="purchases-filters">
              {FILTERS.map((f) => (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`purchase-filter-${f.key}`}
                  className={`ss-btn ss-btn-sm ${filter === f.key ? "ss-btn-primary" : "ss-btn-ghost"}`}>{f.label}</button>
              ))}
            </div>
          </div>
          {filtered.length === 0 ? (
            <Empty icon={Search} title="No matching purchases" sub="Try a different search or filter." />
          ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="purchases-list">
          {filtered.map((o) => (
            <div className="ss-card" style={{ display: "flex", alignItems: "center", gap: 16, padding: 14, flexWrap: "wrap" }} key={o.id} data-testid={`purchase-${o.id}`}>
              <img src={o.image} alt={o.item_title} style={{ width: 72, height: 72, borderRadius: 12, objectFit: "cover" }} />
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 800 }}>{o.item_title}</div>
                <div className="ss-muted" style={{ fontSize: 13, marginTop: 3 }}>Sold by {o.seller_name}</div>
                {o.tracking_number && <div className="ss-muted" style={{ fontSize: 12, marginTop: 3 }}>Tracking: {o.carrier ? `${o.carrier} · ` : ""}{o.tracking_number}</div>}
                {o.status === "shipped" && o.shipped_at && (
                  <div className="ss-muted" style={{ fontSize: 12, marginTop: 3 }} data-testid={`auto-confirm-note-${o.id}`}>
                    Auto-confirms delivered on {new Date(new Date(o.shipped_at).getTime() + 10 * 864e5).toLocaleDateString()} if you don't confirm.
                  </div>
                )}
                {o.return_status && <div style={{ marginTop: 6 }}><span className="ss-pill pill--refunded">Return {o.return_status}</span></div>}
                {o.status === "cancelled" && o.cancel_reason && (
                  <div style={{ marginTop: 6, fontSize: 13, color: "var(--red)" }} data-testid={`purchase-cancel-reason-${o.id}`}>
                    Cancelled by seller — {o.cancel_reason}{o.refund_amount ? ` · Refunded ${money(o.refund_amount)}` : ""}
                  </div>
                )}
                {o.return_tracking_number && <div className="ss-muted" style={{ fontSize: 12, marginTop: 3 }}>Prepaid return: {o.return_carrier} · {o.return_tracking_number}</div>}
                {o.review && (
                  <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }} data-testid={`purchase-review-${o.id}`}>
                    <Stars value={o.review.rating} size={14} />
                    <span className="ss-muted" style={{ fontSize: 12 }}>Your review</span>
                  </div>
                )}
                {o.return_eligible && (
                  <div className="ss-muted" style={{ fontSize: 12, marginTop: 4 }}>Returnable until {new Date(o.return_deadline).toLocaleDateString()}</div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 800, fontSize: 18 }}>{money(o.amount)}</div>
                <div style={{ marginTop: 6 }}><StatusPill status={o.status} /></div>
              </div>
              <div style={{ display: "flex", gap: 8, width: "100%", justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => printReceipt(o)} data-testid={`receipt-${o.id}`}><Receipt size={14} /> Download receipt</button>
                <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => navigate(`/messages/${o.seller_id}`)} data-testid={`message-seller-${o.id}`}><MessageSquare size={14} /> Message seller</button>
                {o.tracking_number && (
                  <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => navigate(`/track/${o.tracking_number}`)} data-testid={`track-purchase-${o.id}`}><Truck size={14} /> Track order</button>
                )}
                {o.return_tracking_number && (
                  <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={() => printReturnLabel(o)} data-testid={`return-label-${o.id}`}><Printer size={14} /> Print return label</button>
                )}
                {o.status === "shipped" && (
                  <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={() => confirmReceived(o)} data-testid={`confirm-received-${o.id}`}><CheckCircle2 size={14} /> Mark as received</button>
                )}
                {o.status === "delivered" && (
                  <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={() => setReviewOrder(o)} data-testid={`review-${o.id}`}><Star size={14} /> {o.review ? "Edit review" : "Write a review"}</button>
                )}
                {o.return_eligible && (
                  <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ color: "var(--red)", borderColor: "var(--red)" }} onClick={() => setReturnOrder(o)} data-testid={`return-${o.id}`}><RotateCcw size={14} /> Return / Refund</button>
                )}
              </div>
            </div>
          ))}
          </div>
          )}
        </>
      )}
      {returnOrder && <ReturnModal order={returnOrder} onClose={() => setReturnOrder(null)} onDone={() => { setReturnOrder(null); load(); }} />}
      {reviewOrder && <ReviewModal order={reviewOrder} onClose={() => setReviewOrder(null)} onDone={() => { setReviewOrder(null); load(); }} />}
    </div>
  );
}
